import { useEffect } from 'react';

/**
 * SEOHead Component
 * Dynamically updates meta tags for SEO and social media sharing
 */
const SEOHead = ({ 
  title, 
  description, 
  image, 
  url, 
  type = 'article',
  author = 'Shared Oxygen',
  publishedTime,
  modifiedTime,
  tags = []
}) => {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = `${title} | Shared Oxygen`;
    }

    // Helper to update or create meta tag
    const updateMetaTag = (property, content, isName = false) => {
      if (!content) return;
      
      const attribute = isName ? 'name' : 'property';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, property);
      document.head.appendChild(element);
    }

    element.setAttribute('content', content);
  };

  // Determine the correct image MIME type
    const deriveImageType = (url) => {
    if (!url) return null;
    const parsed = url.split('?')[0].toLowerCase();
    if (parsed.endsWith('.svg')) return 'image/svg+xml';
    if (parsed.endsWith('.png')) return 'image/png';
    if (parsed.endsWith('.gif')) return 'image/gif';
    if (parsed.endsWith('.webp')) return 'image/webp';
    if (parsed.endsWith('.avif')) return 'image/avif';
    return 'image/jpeg';
  };

    const siteOrigin =
      import.meta.env?.VITE_SITE_URL ||
      (typeof window !== 'undefined' && window.location?.origin) ||
      'https://sharedoxygen.com';

    const toAbsoluteUrl = (path) => {
      if (!path) return null;
      return path.startsWith('http') ? path : `${siteOrigin}${path}`;
    };

    const resolveOgImage = (path) => {
      const absolute = toAbsoluteUrl(path);
      if (!absolute) return { og: null, original: null };

      if (absolute.toLowerCase().includes('.svg')) {
        const pngCandidate = absolute.replace(/\.svg(\?.*)?$/i, '.png$1');
        return { og: pngCandidate, original: absolute };
      }

      return { og: absolute, original: absolute };
    };

    // Basic meta tags
    updateMetaTag('description', description, true);
    
    // Open Graph meta tags (Facebook, LinkedIn, etc.)
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:type', type);
    updateMetaTag('og:url', url || window.location.href);
    updateMetaTag('og:site_name', 'Shared Oxygen');
    
    // Image tag - ensure absolute URL for social media crawlers
    if (image) {
      const { og: ogImageUrl, original: originalImage } = resolveOgImage(image);
      const metaImage = ogImageUrl || originalImage;

      if (metaImage) {
        updateMetaTag('og:image', metaImage);
        updateMetaTag('og:image:secure_url', metaImage);
        updateMetaTag('og:image:width', '1920');
        updateMetaTag('og:image:height', '1080');
        const imageType = deriveImageType(metaImage);
      updateMetaTag('og:image:type', imageType);
        const imageAlt = title || 'Shared Oxygen Article';
        updateMetaTag('og:image:alt', imageAlt);

        // Twitter cards
        updateMetaTag('twitter:image', metaImage, true);
        updateMetaTag('twitter:image:alt', imageAlt, true);
      }
    }

    // Twitter Card meta tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    if (image) {
      const absoluteImage = image.startsWith('http') 
        ? image 
        : `${window.location.origin}${image}`;
      updateMetaTag('twitter:image', absoluteImage, true);
    }

    // Article specific meta tags
    if (type === 'article') {
      updateMetaTag('article:author', author);
      if (publishedTime) {
        updateMetaTag('article:published_time', publishedTime);
      }
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime);
      }
      
      // Article tags
      if (tags.length > 0) {
        // Remove existing article:tag meta tags
        document.querySelectorAll('meta[property="article:tag"]').forEach(el => el.remove());
        
        // Add new tags
        tags.forEach(tag => {
          const tagElement = document.createElement('meta');
          tagElement.setAttribute('property', 'article:tag');
          tagElement.setAttribute('content', tag);
          document.head.appendChild(tagElement);
        });
      }
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', url || window.location.href);

  }, [title, description, image, url, type, author, publishedTime, modifiedTime, tags]);

  return null; // This component doesn't render anything
};

export default SEOHead;

